import { type Promotion } from 'types/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { type FileImageRequest } from 'types/fileUpload'

interface UsePutPromotionProps {
  id: number
  onSuccess?: (data: Promotion, variables: FileImageRequest) => Promise<void>
  onError?: (error: Error, variables: FileImageRequest) => void
}

export const usePutImagePromotion = ({
  id,
  onSuccess,
  onError,
}: UsePutPromotionProps) => {
  const putAPIClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (promotion: FileImageRequest) => {
      const body = new FormData()
      body.set('FileUpload', promotion.ImageFile)
      body.set('FileUploadName', promotion.ImageFileName)
      body.set('FileUploadType', promotion.ImageFileType)

      const { data, error } = await putAPIClient.put(
        '/admin/promotions/{id}/upload',
        {
          params: { path: { id } },
          body: {},
          bodySerializer: () => body,
        }
      )
      return { data, error }
    },
    onSuccess: async ({ data, error }, variables: FileImageRequest) => {
      await queryClient.invalidateQueries({
        queryKey: ['/promotions/{id}/upload', id],
      })
      if (error !== undefined) {
        onError?.(error as Error, variables)
      } else {
        await onSuccess?.(data as Promotion, variables)
      }
    },
    onError,
  })
}
